declare global {
    interface Window {
        __API_BASE_URL__?: string;
        __API_ORIGIN__?: string;
        __PUSHER_KEY__?: string;
        __PUSHER_CLUSTER__?: string;
        __TENANT_ORIGIN__?: string;
        __CUSTOMER_ORIGIN__?: string;
        __RIDER_ORIGIN__?: string;
        __NODE_ENV__?: string;
    }

    interface ImportMetaEnv {
        readonly VITE_API_BASE_URL?: string;
        readonly VITE_API_ORIGIN?: string;
        readonly VITE_PUSHER_KEY?: string;
        readonly VITE_PUSHER_CLUSTER?: string;
        readonly VITE_TENANT_ORIGIN?: string;
        readonly VITE_CUSTOMER_ORIGIN?: string;
        readonly VITE_RIDER_ORIGIN?: string;
        readonly VITE_ENABLE_PERF?: string;
    }

    interface ImportMeta {
        readonly env: ImportMetaEnv;
    }
}

function readViteEnv(key: keyof ImportMetaEnv): string | undefined {
    if (typeof import.meta !== "undefined" && import.meta.env) {
        const value = import.meta.env[key];
        if (typeof value === "string" && value.length > 0) {
            return value;
        }
    }
    return undefined;
}

function readWindowString(key: keyof Window): string | undefined {
    if (typeof window !== "undefined") {
        const value = window[key];
        if (typeof value === "string" && value.length > 0) {
            return value;
        }
    }
    return undefined;
}

function stripTrailingSlash(value: string): string {
    return value.replace(/\/$/, "");
}

export function getApiBaseUrl(): string {
    return (
        readViteEnv("VITE_API_BASE_URL") ??
        readWindowString("__API_BASE_URL__") ??
        "http://localhost:4001/api/v1"
    );
}

export function getApiOrigin(): string {
    const fromEnv =
        readViteEnv("VITE_API_ORIGIN") ?? readWindowString("__API_ORIGIN__");

    if (fromEnv) {
        return stripTrailingSlash(fromEnv);
    }

    const base = stripTrailingSlash(getApiBaseUrl());
    const stripped = base.replace(/\/api\/v1(?:\/.*)?$/, "");
    if (stripped && stripped !== base) {
        return stripped;
    }

    try {
        return new URL(base).origin;
    } catch {
        return "http://localhost:4001";
    }
}

export function getTenantOrigin(): string {
    return stripTrailingSlash(
        readViteEnv("VITE_TENANT_ORIGIN") ??
            readWindowString("__TENANT_ORIGIN__") ??
            "http://localhost:3002"
    );
}

export function getCustomerOrigin(): string {
    return stripTrailingSlash(
        readViteEnv("VITE_CUSTOMER_ORIGIN") ??
            readWindowString("__CUSTOMER_ORIGIN__") ??
            "http://localhost:3004"
    );
}

export function getRiderOrigin(): string {
    return stripTrailingSlash(
        readViteEnv("VITE_RIDER_ORIGIN") ??
            readWindowString("__RIDER_ORIGIN__") ??
            "http://localhost:3003"
    );
}

export function getPusherKey(): string {
    return (
        readViteEnv("VITE_PUSHER_KEY") ??
        readWindowString("__PUSHER_KEY__") ??
        "d34b38ae75687d82aa50"
    );
}

export function getPusherCluster(): string {
    return (
        readViteEnv("VITE_PUSHER_CLUSTER") ??
        readWindowString("__PUSHER_CLUSTER__") ??
        "ap2"
    );
}

export function isPerfEnabled(): boolean {
    const flag = readViteEnv("VITE_ENABLE_PERF");
    return flag === "true" || flag === "1";
}

export function resolvePublicAssetUrl(path?: string | null): string | undefined {
    if (!path) {
        return undefined;
    }

    const trimmed = String(path).trim();
    if (!trimmed) {
        return undefined;
    }

    if (/^https?:\/\//i.test(trimmed)) {
        return trimmed;
    }

    const normalized = trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    return `${getApiOrigin()}${normalized}`;
}
